<script setup lang="ts">
import { MediaImageFragment } from '~/api/graphql/generated'
import { Link } from '~/types'

export interface HeroProps {
  logo?: MediaImageFragment
  image?: {
    small?: MediaImageFragment
    large?: MediaImageFragment
  }
  breadcrumbs?: Link[]
  back?: Link
  content: {
    title: string
    subtitle?: string
    intro?: string
  }
}
defineProps<HeroProps>()
</script>

<template>
  <Section v-animate="{ cluster: 'true' }" :class="['section-hero', 'grid', { 'hero-image': image }]">
    <div class="background">
      <Picture
        v-if="image"
        v-animate="{ animation: 'vertical-reveal' }"
        class="image"
        :small="image.small"
        :large="image.large"
        type="hero"
        :loading="'eager'"
        fetchpriority="high"
        :preload="true"
      />
      <Image
        v-if="logo"
        v-animate="{ animation: 'vertical-reveal' }"
        :entity="logo"
        type="teaser-main-menu"
        :loading="'eager'"
        class="logo"
      />
    </div>

    <div class="content t-11 ds-10">
      <Breadcrumbs v-if="breadcrumbs" v-animate="{ animation: 'vertical-reveal' }" :items="breadcrumbs" />
      <ButtonBack v-if="back" v-animate="{ animation: 'vertical-reveal' }" :to="back.url">
        {{ back.text }}
      </ButtonBack>
      <h1 v-animate="{ animation: 'vertical-reveal' }" class="title">
        {{ content.title }}
      </h1>
      <p v-animate="{ animation: 'vertical-reveal' }" class="subtitle">
        {{ content.subtitle }}
      </p>
      <div
        v-if="content.intro"
        v-animate="{ animation: 'vertical-reveal' }"
        class="intro text-intro"
        v-html="content.intro"
      />
    </div>
  </Section>
</template>
<style scoped lang="scss">
.hero {
  display: grid;
  margin-bottom: 0;

  &-image {
    grid-template-rows: auto var(--overlap) 1fr;

    @include fluid(--overlap, 48px, 128px);
    .background {
      grid-column: 1 / -1;
      grid-row: 1 / 3;
      position: relative;
      z-index: -1;
      @include breakpoint(t down) {
        width: calc(50% + 50vw);
      }
    }
    .logo {
      grid-column: 1 / -1;
      grid-row: 1;

      position: absolute;
      top: 24px;
      left: 24px;

      max-width: 140px;
      @include breakpoint(t) {
        top: 40px;
        left: 40px;
      }
      @include breakpoint(d) {
        left: 80px;
      }
    }
    .content {
      grid-column-start: 1;
      grid-row: 2 / 4;
      margin-top: 0;
      background-color: var(--c-white);

      @include fluid(padding-top, 24px, 40px);
      .breadcrumbs,
      .title,
      .subtitle,
      .intro {
        @include breakpoint(t) {
          grid-column-end: -2;
        }
      }
    }
  }
}
.image {
  position: relative;
  display: flex;
  width: 100%;
  @include fluid(height, 380px, 620px);

  > :deep(img) {
    @include fill;
  }
}

.content {
  display: grid;
  gap: 0 var(--gap);
  grid-template-columns: 100%;

  @include fluid(margin-top, 24px, 96px);
  @include breakpoint(t) {
    grid-template-columns: repeat(11, minmax(0, 1fr));
  }
  @include breakpoint(ds) {
    grid-template-columns: repeat(10, minmax(0, 1fr));
  }

  .breadcrumbs,
  .back,
  .title,
  .subtitle,
  .intro {
    grid-column-start: 1;
    grid-column-end: -1;
    @include breakpoint(t) {
      grid-column-start: 2;
    }
    @include breakpoint(tl) {
      grid-column-start: 1;
    }
    @include breakpoint(ds) {
      grid-column-start: 2;
    }
  }
  .intro {
    @include breakpoint(tl) {
      grid-column-end: -2;
    }
  }
}

.breadcrumbs,
.back {
  @include fluid(margin-bottom, 30px, 40px);
}
.title,
.subtitle {
  margin-bottom: 0;
}

.subtitle {
  font-weight: bold;
  color: var(--c-primary);
  @include f-24;
}

.intro {
  @include fluid(margin-top, 30px, 64px);
}
</style>
